import { FagmoblerAlgoliaProduct } from '@fagmobler/types';
import { NextRouter } from 'next/router';
import {
  ConfigureProps,
  InstantSearchServerState,
} from 'react-instantsearch-hooks-web';
import { searchClient } from './searchClient';
import {
  createArticleGroupFromArticleGroupSlugs,
  createArticleGroupSlugFromArticleGroupSlugs,
  createCategoryFromCategorySlugs,
  createCategorySlugFromCategorySlugs,
  createHierarchicalMenuFromSlugs,
} from './utils';
import {
  MaterializedContentBlock,
  SimpleMaterializedArticlePage,
} from '@fagmobler/sanity';

export type CrateSearchPropsProps = {
  rootPath: string;
  slugs: string[];
  categoryType: 'categories' | 'articleGroup';
  serverUrl: string;
};

export type SearchProps = {
  rootPath: string;
  serverUrl: string | null;
  slugs?: string[] | null;

  // Optional
  categoryRootPath: string | null;
  categorySlugsRootPath: string | null;
  facetFilters: ConfigureProps['facetFilters'] | null;
  showCategories: boolean | null;
  placeholder: string | null;
  searchServerState: InstantSearchServerState | null;
  hierarchicalMenu: { [key: string]: string[] } | null;
  categoryType: CrateSearchPropsProps['categoryType'] | null;
  nextRouter: NextRouter | null;
  categories: { [key: string]: string | string[] } | null;
  categorySlugs: { [key: string]: string | string[] } | null;
  articleGroups: { [key: string]: string | string[] } | null;
  articleGroupSlugs: { [key: string]: string | string[] } | null;
  algoliaProductCategory: string | null;
  articleGroupsRootPath: string | null;
  articleGroupSlugsRootPath: string | null;
  sessionStorageCache: any | null;
  contentBlocks?: MaterializedContentBlock[] | null;
  relatedArticles?: SimpleMaterializedArticlePage[] | null;
};

export const getFacetFilterSlugs = (
  slugs: string[],
  categoryType: 'categories' | 'articleGroup'
) => {
  if (categoryType === 'categories') {
    return slugs
      .map((_, i, arr) => arr.slice(0, i + 1).join(' > '))
      .map((s, i) => `categorySlugs.lvl${i}:${s}`)
      .map((s) => [s]);
  }
  if (categoryType === 'articleGroup') {
    return slugs
      .map((_, i, arr) => arr.slice(0, i + 1).join(' > '))
      .map((s, i) => `articleGroupSlugs.lvl${i}:${s}`)
      .map((s) => [s]);
  }
};

export const createCategorySearchProps = async ({
  rootPath,
  slugs,
  categoryType,
  serverUrl,
}: CrateSearchPropsProps) => {
  const indexes = {
    categories: 'try_fagmobler_categorySlugs',
    articleGroup: 'try_fagmobler_articleGroupSlugs',
  };

  // Const get a random product that is in this category
  const index = searchClient.initIndex(indexes[categoryType]);

  const facetFiltersSlugs = getFacetFilterSlugs(slugs, categoryType);
  const hasSlugs = slugs.filter(Boolean).length > 0;

  const hit = hasSlugs
    ? await index
        .search<FagmoblerAlgoliaProduct>('', {
          facetFilters: facetFiltersSlugs,
        })
        .then((result) => {
          return result.hits[0] || {};
        })
    : null;

  const categories = categoryType === 'categories' ? hit?.categories : null;
  const categorySlugs =
    categoryType === 'categories' && hasSlugs ? hit?.categorySlugs : null;
  const articleGroups =
    categoryType === 'articleGroup' && hasSlugs ? hit?.articleGroups : null;
  const articleGroupSlugs =
    categoryType === 'articleGroup' && hasSlugs ? hit?.articleGroupSlugs : null;

  // No category. Return null and make the [...slugs] return 404
  // if (categoryType === 'categories' && (!categories || !categorySlugs))
  //   return null;
  // if (categoryType === 'articleGroup' && (!articleGroups || !articleGroupSlugs))
  //   return null;

  // Resolve category from categories and categoryslugs
  const category =
    categories &&
    categorySlugs &&
    createCategoryFromCategorySlugs({
      slugs,
      categories,
      categorySlugs,
    });
  // Resolve categorySlug from categories and categoryslugs
  const categorySlug =
    categorySlugs &&
    createCategorySlugFromCategorySlugs({
      slugs,
      categorySlugs,
    });

  // Resolve category from categories and categoryslugs
  const articleGroup =
    articleGroups &&
    articleGroupSlugs &&
    createArticleGroupFromArticleGroupSlugs({
      slugs,
      articleGroups,
      articleGroupSlugs,
    });

  // Resolve articleGroupSlug from articleGroupSlugs and slugs
  const articleGroupSlug =
    articleGroupSlugs &&
    createArticleGroupSlugFromArticleGroupSlugs({
      slugs,
      articleGroupSlugs,
    });

  // Deepest cateogory
  const algoliaProductCategory = (category || articleGroup || []).slice(-1)[0];

  const categoryFacetFilters =
    category && categorySlug
      ? [
          ...category?.map((s, i) => `categories.lvl${i}:${s}`).map((s) => [s]),
          ...categorySlug
            ?.map((s, i) => `categorySlugs.lvl${i}:${s}`)
            .map((s) => [s]),
        ]
      : [];

  const articleGrupFacetFilters =
    articleGroup && articleGroupSlug
      ? [
          ...articleGroup
            ?.map((s, i) => `articleGroups.lvl${i}:${s}`)
            .map((s) => [s]),
          ...articleGroupSlug
            ?.map((s, i) => `articleGroupSlugs.lvl${i}:${s}`)
            .map((s) => [s]),
        ]
      : [];

  // Transform category to facetfilters
  const facetFilters = [...categoryFacetFilters, ...articleGrupFacetFilters];

  const currentCategorySlugs = categorySlug
    ? categorySlug.map((cat, i) => {
        return `categorySlugs.lvl${i}: ${cat}`;
      })
    : null;

  // Rootpath example: "stue > sofa"
  const categorySlugsRootPath = currentCategorySlugs
    ? currentCategorySlugs?.slice(-1)[0]?.split(': ')[1] || null
    : null;

  // Flat array of current categories
  const currentCategories = category
    ? category.map((cat, i) => {
        return `categories.lvl${i}: ${cat}`;
      })
    : null;

  const currentArticleGroups = articleGroup
    ? articleGroup.map((cat, i) => {
        return `articleGroups.lvl${i}: ${cat}`;
      })
    : null;

  const currentArticleGroupSlugs = articleGroupSlug
    ? articleGroupSlug.map((cat, i) => {
        return `articleGroupSlugs.lvl${i}: ${cat}`;
      })
    : null;

  // Rootpath example: "Stue > Sofa"
  const categoryRootPath = currentCategories
    ? currentCategories.slice(-1)[0]?.split(': ')[1] || null
    : null;

  const articleGroupsRootPath = currentArticleGroups
    ? currentArticleGroups.slice(-1)[0]?.split(': ')[1] || null
    : null;

  const articleGroupSlugsRootPath = currentArticleGroupSlugs
    ? currentArticleGroupSlugs.slice(-1)[0]?.split(': ')[1] || null
    : null;

  const hierarchicalMenu =
    slugs && createHierarchicalMenuFromSlugs(slugs, categoryType);

  const searchProps: SearchProps = {
    serverUrl,
    rootPath,
    placeholder: 'Søk…',
    // facetFilters: facetFilters?.length > 0 ? facetFilters : null,
    facetFilters: null,
    searchServerState: null,
    categoryType: categoryType || null,
    hierarchicalMenu: hierarchicalMenu || null,

    algoliaProductCategory: algoliaProductCategory || null,
    articleGroups: articleGroups || null,
    articleGroupSlugs: articleGroupSlugs || null,
    categories: categories || null,
    categorySlugs: categorySlugs || null,

    // Root paths
    categorySlugsRootPath: categorySlugsRootPath || null,
    categoryRootPath: categoryRootPath || null,
    articleGroupsRootPath: articleGroupsRootPath || null,
    articleGroupSlugsRootPath: articleGroupSlugsRootPath || null,

    showCategories: true,
    nextRouter: null,
    sessionStorageCache: null,
  };

  return searchProps;
};
